// =============================================
// WorkspaceSwitcher — karta sidebar z aktywnym workspace
// =============================================
// Replacement Popover-based widoku (2026-05-26).
//
// WHY: po Phase 3 per-workspace API keys (feat/workspace-api-keys) workspace
// switcher = kompletny key-set switcher dla 4 providers. Poprzedni Popover
// wygladal jak filtr widoku, NIE komunikowal ze zmiana = inne konto rozliczeniowe.
//
// UX iteracja 2 (2026-05-26):
//   - Border-left 4px (mocniejszy akcent)
//   - Naglowek 2-liniowy: ikona CreditCard + "Konto API" + pastylka "aktywne"
//     w jednym rzedzie, subtitle "Uzywane przy tworzeniu map tematycznych" pod
//   - Wiersz workspace: kolorowy kwadrat 32x32 z ikona + jednowyrazowa nazwa
//     ("Firma" / "SideProjects") zamiast dual-label
//   - Info-blok z ikona Eye: "Widzisz mapy tej kategorii"
//   - Pomaranczowa pastylka z borderem, jasniejszy tekst, bold "klucze API i konto rozliczeniowe"
//   - Button "Zmień workspace" wzmocniony (padding, font, border, bg)
//
// Collapsed mode: tylko ikona + zielona kropka (oszczednosc miejsca), klik → modal.
// =============================================

import { useState } from "react";
import { AlertTriangle, ArrowLeftRight, Eye, Network } from "lucide-react";
import { useWorkspace, type Workspace } from "@/hooks/useWorkspace";
import {
  WORKSPACE_COLORS,
  WORKSPACE_ICONS,
  WORKSPACE_SHORT_NAMES,
} from "./workspaceTheme";
import { WorkspaceSwitchDialog } from "./WorkspaceSwitchDialog";
import { confirmLeaveDuringTransfer } from "@/lib/transferRunStore";

interface WorkspaceSwitcherProps {
  /** Collapsed sidebar style (pokazuje tylko ikone + zielona kropka, bez karty) */
  collapsed?: boolean;
}

export function WorkspaceSwitcher({ collapsed = false }: WorkspaceSwitcherProps) {
  const { workspace, setWorkspace, meta, all } = useWorkspace();
  const [open, setOpen] = useState(false);

  const colors = WORKSPACE_COLORS[workspace as Workspace];
  const Icon = WORKSPACE_ICONS[workspace as Workspace];
  const shortName = WORKSPACE_SHORT_NAMES[workspace as Workspace];

  const handleSelect = (next: Workspace) => {
    // Zmiana workspace moze zrobic full reload — w trakcie kreatora "Przenies domeny" pytamy.
    if (next !== workspace && !confirmLeaveDuringTransfer()) { setOpen(false); return; }
    setWorkspace(next);
    setOpen(false);
  };

  // Collapsed mode: tylko ikona + zielona kropka. Klik otwiera modal (full UX).
  if (collapsed) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full flex flex-col items-center gap-1 px-1 py-2 rounded-md hover:bg-muted/60 transition"
          title={meta.label}
        >
          <Icon className="h-5 w-5" style={{ color: colors.accent }} />
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
        </button>
        <WorkspaceSwitchDialog
          open={open}
          onOpenChange={setOpen}
          currentWorkspace={workspace}
          workspaces={all}
          onSelect={handleSelect}
        />
      </>
    );
  }

  return (
    <>
      <div
        className="rounded-md p-3 bg-sidebar"
        style={{ borderLeft: `4px solid ${colors.accent}` }}
      >
        {/* ===== WORKSPACE ROW — kolorowy kwadrat + nazwa + pastylka aktywne + subtitle ===== */}
        <div className="flex items-start gap-3 mb-3">
          <div
            className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: colors.dark }}
          >
            <Icon
              className="h-[18px] w-[18px]"
              style={{ color: colors.accent }}
            />
          </div>
          <div className="flex-1 min-w-0">
            {/* Linia 1: nazwa + pastylka "aktywne" */}
            <div className="flex items-center justify-between gap-2">
              <span
                className="text-[15px] font-medium leading-none"
                style={{ color: colors.accent }}
              >
                {shortName}
              </span>
              <div
                className="flex items-center gap-1 px-[7px] py-0.5 rounded-full shrink-0"
                style={{ backgroundColor: "#0a2418" }}
              >
                <span
                  className="h-[5px] w-[5px] rounded-full"
                  style={{ backgroundColor: "#5DCAA5" }}
                />
                <span
                  className="text-[10px] font-medium"
                  style={{ color: "#5DCAA5" }}
                >
                  aktywne
                </span>
              </div>
            </div>
            {/* Linia 2: ikona Network + tekst "Konto API uzywane przy tworzeniu map tematycznych" */}
            <div className="flex items-center gap-1.5 mt-1.5">
              <Network className="h-3 w-3 shrink-0" style={{ color: "#888" }} />
              <span className="text-[11px] leading-snug" style={{ color: "#888" }}>
                Konto API używane przy tworzeniu map tematycznych
              </span>
            </div>
          </div>
        </div>

        {/* ===== INFO-BLOK — "Widzisz mapy tej kategorii" ===== */}
        <div
          className="flex items-center gap-[7px] mb-3"
          style={{
            backgroundColor: "#0f0f0f",
            padding: "8px 10px",
            borderRadius: "6px",
          }}
        >
          <Eye className="h-3 w-3 shrink-0" style={{ color: "#888" }} />
          <span className="text-[11px]" style={{ color: "#cfcfcf" }}>
            Widzisz mapy tej kategorii
          </span>
        </div>

        {/* ===== POMARAŃCZOWA PASTYLKA OSTRZEŻENIA ===== */}
        <div
          className="rounded-md flex items-start gap-2 mb-3"
          style={{
            backgroundColor: "#2a1f0a",
            border: "0.5px solid #634a0a",
            padding: "9px 10px",
          }}
        >
          <AlertTriangle
            className="h-[14px] w-[14px] shrink-0 mt-0.5"
            style={{ color: "#EF9F27" }}
          />
          <span
            className="text-[11px] leading-snug"
            style={{ color: "#FCD08A" }}
          >
            Zmiana ={" "}
            <span style={{ color: "#FAEEDA", fontWeight: 500 }}>
              inne klucze API i konto rozliczeniowe
            </span>
            .
          </span>
        </div>

        {/* ===== BUTTON "Zmień workspace" ===== */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-center gap-2 rounded-md transition hover:bg-zinc-800/70"
          style={{
            padding: "8px 12px",
            fontSize: "13px",
            fontWeight: 500,
            color: "#e0e0e0",
            backgroundColor: "#1a1a1a",
            border: "0.5px solid #4a4a4a",
          }}
        >
          <ArrowLeftRight className="h-[15px] w-[15px]" />
          <span>Zmień workspace</span>
        </button>
      </div>

      <WorkspaceSwitchDialog
        open={open}
        onOpenChange={setOpen}
        currentWorkspace={workspace}
        workspaces={all}
        onSelect={handleSelect}
      />
    </>
  );
}
